import React from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Typography } from "@mui/material";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import GripIcon from "@icons/myicons/grip-vertical.svg?react";
import {
  InvestmentListView,
  InvestmentMetricKey,
  InvestmentSortField,
  METRIC_ICONS,
} from "@features/investments/investment/components/InvestmentTable/mobile";

const Row = styled("div", {
  shouldForwardProp: (prop) => prop !== "isDragging",
})<{ isDragging: boolean }>(({ theme, isDragging }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  minHeight: theme.spacing(10),
  padding: theme.spacing(0, 2, 0, 4),
  position: "relative",
  zIndex: isDragging ? 1 : "auto",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: isDragging ? theme.palette.background.paper : "transparent",
  boxShadow: isDragging ? theme.shadows[2] : "none",
}));

const SortableMetricRow = ({
  id,
  label,
}: {
  id: InvestmentMetricKey;
  label: string;
}) => {
  const { t } = useTranslation();
  const Icon = METRIC_ICONS[id];
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <Row
      ref={setNodeRef}
      isDragging={isDragging}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      data-testid={`metric-order-${id}`}
    >
      <Box component="span" display="flex" alignItems="center" gap={2}>
        <Box
          component="span"
          display="inline-flex"
          color="text.secondary"
          aria-hidden
        >
          <Icon width={16} height={16} />
        </Box>
        <Typography variant="body3" component="span">
          {label}
        </Typography>
      </Box>
      <IconButton
        size="small"
        ref={setActivatorNodeRef}
        aria-label={t("domains.investments.investmentTable.mobile.reorder", {
          field: label,
        })}
        sx={{
          flex: "none",
          color: "icon.tertiary",
          touchAction: "none",
          cursor: isDragging ? "grabbing" : "grab",
        }}
        {...attributes}
        {...listeners}
      >
        <GripIcon width="16px" height="16px" />
      </IconButton>
    </Row>
  );
};

interface Props {
  view: InvestmentListView;
  fieldLabel: (field: InvestmentSortField) => string;
}

export const MetricOrderList = ({ view, fieldLabel }: Props) => {
  const { metricOrder, moveMetric } = view;
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    moveMetric(
      metricOrder.findIndex((key) => key === active.id),
      metricOrder.findIndex((key) => key === over.id)
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
    >
      <SortableContext
        items={metricOrder}
        strategy={verticalListSortingStrategy}
      >
        <div>
          {metricOrder.map((key) => (
            <SortableMetricRow key={key} id={key} label={fieldLabel(key)} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};
